/**
 * PublicIntakePage - Sprint 11
 * Public demand intake form for external business users
 * No authentication required
 * Includes multi-file attachment upload with drag-and-drop
 * Sprint 11 additions: hCaptcha verification, confirmation email
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Send, CheckCircle, AlertCircle, Loader2, Building2, 
  Mail, User, FileText, Clock, ArrowRight, Upload, X,
  File, FileImage, FileSpreadsheet, FileType, Paperclip, Shield
} from 'lucide-react';
import HCaptcha from '@hcaptcha/react-hcaptcha';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// File type icons
const getFileIcon = (type) => {
  if (type?.includes('image')) return <FileImage className="h-5 w-5 text-purple-500" />;
  if (type?.includes('spreadsheet') || type?.includes('excel')) return <FileSpreadsheet className="h-5 w-5 text-green-500" />;
  if (type?.includes('pdf')) return <FileType className="h-5 w-5 text-red-500" />;
  if (type?.includes('presentation') || type?.includes('powerpoint')) return <FileText className="h-5 w-5 text-orange-500" />;
  return <File className="h-5 w-5 text-blue-500" />;
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_TOTAL_SIZE = 30 * 1024 * 1024; // 30MB
const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.png', '.jpg', '.jpeg', '.zip'];

const PublicIntakePage = () => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    department: '',
    requestor_name: '',
    requestor_email: '',
    category: '',
    urgency: 'medium',
    business_justification: ''
  });
  
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  
  // File upload state
  const [files, setFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  
  // CAPTCHA state
  const [captchaConfig, setCaptchaConfig] = useState({ captcha_enabled: false, captcha_site_key: null });
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  useEffect(() => {
    // Fetch intake configuration including CAPTCHA settings
    fetch(`${API_URL}/api/public/intake-config`)
      .then(r => r.json())
      .then(data => {
        setCaptchaConfig(data);
      })
      .catch(err => {
        console.error('Failed to load intake config:', err);
      });
    
    // Fetch departments and categories
    Promise.all([
      fetch(`${API_URL}/api/public/demand-intake/departments`).then(r => r.json()),
      fetch(`${API_URL}/api/public/demand-intake/categories`).then(r => r.json())
    ]).then(([deptData, catData]) => {
      setDepartments(deptData.departments || []);
      setCategories(catData.categories || []);
    }).catch(err => {
      console.error('Failed to load form options:', err);
      // Use defaults
      setDepartments(['Engineering', 'Product', 'Finance', 'HR', 'Marketing', 'Sales', 'Operations', 'IT', 'Other']);
      setCategories([
        { value: 'infrastructure', label: 'Infrastructure' },
        { value: 'application', label: 'Application' },
        { value: 'process', label: 'Process' },
        { value: 'security', label: 'Security' },
        { value: 'other', label: 'Other' }
      ]);
    });
  }, []);

  // Calculate total file size
  const totalFileSize = files.reduce((acc, f) => acc + f.size, 0);

  // Validate file
  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `File type ${ext} not allowed. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size is 10MB`;
    }
    if (totalFileSize + file.size > MAX_TOTAL_SIZE) {
      return `Total upload size exceeds 30MB limit`;
    }
    return null;
  };

  // Handle file selection
  const handleFileSelect = useCallback((selectedFiles) => {
    const newFiles = [];
    const errors = [];
    
    Array.from(selectedFiles).forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else if (!files.some(f => f.name === file.name && f.size === file.size)) {
        newFiles.push(file);
      }
    });
    
    if (errors.length > 0) {
      setErrors(prev => ({ ...prev, files: errors.join('\n') }));
      setTimeout(() => setErrors(prev => ({ ...prev, files: null })), 5000);
    }
    
    if (newFiles.length > 0) {
      setFiles(prev => [...prev, ...newFiles]);
    }
  }, [files, totalFileSize]);

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  }, [handleFileSelect]);

  // Remove file
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors = {};
    
    if (!form.title || form.title.length < 5) {
      newErrors.title = 'Title must be at least 5 characters';
    }
    if (!form.description || form.description.length < 20) {
      newErrors.description = 'Description must be at least 20 characters';
    }
    if (!form.department) {
      newErrors.department = 'Please select a department';
    }
    if (!form.requestor_name || form.requestor_name.length < 2) {
      newErrors.requestor_name = 'Please enter your name';
    }
    if (!form.requestor_email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.requestor_email)) {
      newErrors.requestor_email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Upload files after submission
  const uploadFiles = async (submissionId) => {
    const uploadResults = [];
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API_URL}/api/public/demand-intake/${submissionId}/attachments`, {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          const result = await response.json();
          uploadResults.push({ success: true, filename: file.name, ...result });
        } else {
          const error = await response.json();
          uploadResults.push({ success: false, filename: file.name, error: error.detail });
        }
      } catch (err) {
        uploadResults.push({ success: false, filename: file.name, error: 'Upload failed' });
      }
    }
    
    return uploadResults;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    // Check CAPTCHA if enabled
    if (captchaConfig.captcha_enabled && !captchaToken) {
      setErrors({ submit: 'Please complete the CAPTCHA verification.' });
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Step 1: Submit the demand with CAPTCHA token
      const response = await fetch(`${API_URL}/api/public/demand-intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          captcha_token: captchaToken
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Step 2: Upload attachments if any
        if (files.length > 0) {
          setUploadingFiles(true);
          const uploadResults = await uploadFiles(result.submission_id);
          const successCount = uploadResults.filter(r => r.success).length;
          result.attachment_count = successCount;
          setUploadingFiles(false);
        }
        
        setSubmissionResult(result);
        setSubmitted(true);
      } else if (response.status === 429) {
        setErrors({ submit: 'Too many submissions. Please try again later.' });
      } else if (response.status === 400) {
        const error = await response.json();
        if (error.detail?.includes('CAPTCHA')) {
          setErrors({ submit: 'CAPTCHA verification failed. Please try again.' });
          // Reset captcha
          if (captchaRef.current) {
            captchaRef.current.resetCaptcha();
          }
          setCaptchaToken(null);
        } else {
          setErrors({ submit: error.detail || 'Submission failed. Please try again.' });
        }
      } else {
        const error = await response.json();
        setErrors({ submit: error.detail || 'Submission failed. Please try again.' });
      }
    } catch (err) {
      setErrors({ submit: 'Network error. Please check your connection and try again.' });
    } finally {
      setSubmitting(false);
      setUploadingFiles(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Success state
  if (submitted && submissionResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg text-center">
          <CardContent className="py-12">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Submission Received!
            </h2>
            <p className="text-gray-600 mb-6">
              {submissionResult.message}
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500 mb-1">Your Reference Number</p>
              <p className="text-2xl font-mono font-bold text-indigo-600">
                {submissionResult.submission_id}
              </p>
            </div>
            
            {submissionResult.attachment_count > 0 && (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600 mb-4">
                <Paperclip className="h-4 w-4" />
                <span>{submissionResult.attachment_count} file(s) attached</span>
              </div>
            )}
            
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Clock className="h-4 w-4" />
              <span>Estimated review time: {submissionResult.estimated_review_time}</span>
            </div>
            
            <p className="mt-6 text-sm text-gray-500">
              Save your reference number to check the status of your submission.
            </p>
            
            <Button
              variant="outline"
              className="mt-6"
              data-testid="submit-another-btn"
              onClick={() => {
                setSubmitted(false);
                setSubmissionResult(null);
                setFiles([]);
                setForm({
                  title: '',
                  description: '',
                  department: '',
                  requestor_name: '',
                  requestor_email: '',
                  category: '',
                  urgency: 'medium',
                  business_justification: ''
                });
              }}
            >
              Submit Another Request
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl font-bold text-white">S</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Submit a Business Request
          </h1>
          <p className="text-slate-300">
            Submit your project or initiative request for PMO review
          </p>
        </div>

        {/* Form */}
        <Card className="shadow-2xl" data-testid="public-intake-form">
          <CardHeader>
            <CardTitle>Request Details</CardTitle>
            <CardDescription>
              Fill in the details below. All fields marked with * are required.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Banner */}
              {errors.submit && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <span className="text-red-700">{errors.submit}</span>
                </div>
              )}

              {/* Your Information */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2 text-gray-700">
                  <User className="h-4 w-4" />
                  Your Information
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Your Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={form.requestor_name}
                      onChange={(e) => handleChange('requestor_name', e.target.value)}
                      placeholder="John Smith"
                      className={errors.requestor_name ? 'border-red-500' : ''}
                      data-testid="input-name"
                    />
                    {errors.requestor_name && (
                      <p className="text-red-500 text-xs mt-1">{errors.requestor_name}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="email"
                      value={form.requestor_email}
                      onChange={(e) => handleChange('requestor_email', e.target.value)}
                      placeholder="john@company.com"
                      className={errors.requestor_email ? 'border-red-500' : ''}
                      data-testid="input-email"
                    />
                    {errors.requestor_email && (
                      <p className="text-red-500 text-xs mt-1">{errors.requestor_email}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Request Details */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2 text-gray-700">
                  <FileText className="h-4 w-4" />
                  Request Details
                </h3>
                
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Request Title <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={form.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="Brief title describing your request"
                    className={errors.title ? 'border-red-500' : ''}
                    data-testid="input-title"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-xs mt-1">{errors.title}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Describe your request in detail. What problem are you trying to solve? What is the expected outcome?"
                    rows={5}
                    className={errors.description ? 'border-red-500' : ''}
                    data-testid="input-description"
                  />
                  {errors.description && (
                    <p className="text-red-500 text-xs mt-1">{errors.description}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Department <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={form.department}
                      onChange={(e) => handleChange('department', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md ${errors.department ? 'border-red-500' : 'border-gray-300'}`}
                      data-testid="select-department"
                    >
                      <option value="">Select department...</option>
                      {departments.map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                    {errors.department && (
                      <p className="text-red-500 text-xs mt-1">{errors.department}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Category</label>
                    <select
                      value={form.category}
                      onChange={(e) => handleChange('category', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      data-testid="select-category"
                    >
                      <option value="">Select category...</option>
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Urgency Level</label>
                  <div className="flex gap-3">
                    {['low', 'medium', 'high', 'critical'].map(level => (
                      <label
                        key={level}
                        className={`flex-1 py-2 px-3 border rounded-lg text-center cursor-pointer transition-colors ${
                          form.urgency === level
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="urgency"
                          value={level}
                          checked={form.urgency === level}
                          onChange={(e) => handleChange('urgency', e.target.value)}
                          className="sr-only"
                        />
                        <span className="text-sm capitalize">{level}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Business Justification (optional)
                  </label>
                  <Textarea
                    value={form.business_justification}
                    onChange={(e) => handleChange('business_justification', e.target.value)}
                    placeholder="What is the business value or ROI of this request?"
                    rows={3}
                    data-testid="input-justification"
                  />
                </div>
              </div>

              {/* File Upload Section */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2 text-gray-700">
                  <Paperclip className="h-4 w-4" />
                  Attachments (optional)
                </h3>
                <p className="text-sm text-gray-500">
                  Attach screenshots, spreadsheets, or docs that show your expected output. 
                  Supported types: PDF, Word, Excel, PowerPoint, images, ZIP. Max 10 MB/file, 30 MB total.
                </p>
                
                {/* Drag and Drop Zone */}
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragActive 
                      ? 'border-indigo-500 bg-indigo-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  data-testid="file-drop-zone"
                >
                  <Upload className={`h-8 w-8 mx-auto mb-2 ${dragActive ? 'text-indigo-500' : 'text-gray-400'}`} />
                  <p className="text-sm text-gray-600 mb-2">
                    Drag and drop files here, or{' '}
                    <button
                      type="button"
                      className="text-indigo-600 hover:text-indigo-700 font-medium"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      browse
                    </button>
                  </p>
                  <p className="text-xs text-gray-500">
                    Max 10MB per file, 30MB total. Allowed: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, PNG, JPG, ZIP
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    accept={ALLOWED_EXTENSIONS.join(',')}
                    onChange={(e) => handleFileSelect(e.target.files)}
                    data-testid="file-input"
                  />
                </div>
                
                {/* File Error */}
                {errors.files && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 whitespace-pre-line">
                    {errors.files}
                  </div>
                )}
                
                {/* File List */}
                {files.length > 0 && (
                  <div className="space-y-2" data-testid="file-list">
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>{files.length} file(s) selected</span>
                      <span>{formatFileSize(totalFileSize)} / 30MB</span>
                    </div>
                    {files.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        data-testid={`file-item-${index}`}
                      >
                        <div className="flex items-center gap-3">
                          {getFileIcon(file.type)}
                          <div>
                            <p className="text-sm font-medium text-gray-700 truncate max-w-xs">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="p-1 hover:bg-gray-200 rounded"
                          onClick={() => removeFile(index)}
                          data-testid={`remove-file-${index}`}
                        >
                          <X className="h-4 w-4 text-gray-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* hCaptcha */}
              {captchaConfig.captcha_enabled && captchaConfig.captcha_site_key && (
                <div className="flex flex-col items-center pt-4 border-t">
                  <div className="flex items-center gap-2 mb-3 text-sm text-gray-500">
                    <Shield className="h-4 w-4" />
                    <span>Please verify you're human</span>
                  </div>
                  <HCaptcha
                    ref={captchaRef}
                    sitekey={captchaConfig.captcha_site_key}
                    onVerify={(token) => setCaptchaToken(token)}
                    onExpire={() => setCaptchaToken(null)}
                    onError={() => setCaptchaToken(null)}
                  />
                </div>
              )}

              {/* Submit */}
              <div className="pt-4 border-t">
                <Button
                  type="submit"
                  className="w-full h-12 text-base"
                  disabled={submitting || uploadingFiles || (captchaConfig.captcha_enabled && !captchaToken)}
                  data-testid="submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      {uploadingFiles ? 'Uploading files...' : 'Submitting...'}
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Submit Request {files.length > 0 && `(${files.length} attachment${files.length > 1 ? 's' : ''})`}
                    </>
                  )}
                </Button>
                <p className="text-xs text-center text-gray-500 mt-3">
                  By submitting, you agree that your request will be reviewed by our PMO team.
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Process Info */}
        <div className="mt-8 text-center">
          <h3 className="text-white font-medium mb-4">What happens next?</h3>
          <div className="flex items-center justify-center gap-4 text-slate-300 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs">1</div>
              <span>Submit</span>
            </div>
            <ArrowRight className="h-4 w-4" />
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-slate-600 text-white flex items-center justify-center text-xs">2</div>
              <span>PMO Review</span>
            </div>
            <ArrowRight className="h-4 w-4" />
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-slate-600 text-white flex items-center justify-center text-xs">3</div>
              <span>Assessment</span>
            </div>
            <ArrowRight className="h-4 w-4" />
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-slate-600 text-white flex items-center justify-center text-xs">4</div>
              <span>Decision</span>
            </div>
          </div>
        </div>

        <p className="text-center text-slate-400 text-sm mt-8">
          &copy; 2025 ScopeOps. Portfolio Visibility Platform.
        </p>
      </div>
    </div>
  );
};

export default PublicIntakePage;
